import i18n from "i18next";
import { Trans } from "react-i18next";

import { ReactComponent as BedSvg } from "@/assets/icons/emojis/bed.svg";
import { ReactComponent as BrainSvg } from "@/assets/icons/emojis/brain.svg";
import { ReactComponent as CalendarSvg } from "@/assets/icons/emojis/calendar.svg";
import { ReactComponent as CalendarWithDateSvg } from "@/assets/icons/emojis/calendar-with-date.svg";
import { ReactComponent as ClockSvg } from "@/assets/icons/emojis/clock.svg";
import { ReactComponent as ConfusedFemaleSvg } from "@/assets/icons/emojis/confused-female.svg";
import { ReactComponent as DressSvg } from "@/assets/icons/emojis/dress.svg";
import { ReactComponent as EnergySvg } from "@/assets/icons/emojis/energy.svg";
import { ReactComponent as ExcitedSvg } from "@/assets/icons/emojis/excited.svg";
import { ReactComponent as FamilySvg } from "@/assets/icons/emojis/family.svg";
import { ReactComponent as FireSvg } from "@/assets/icons/emojis/fire.svg";
import { ReactComponent as HomeSvg } from "@/assets/icons/emojis/home.svg";
import { ReactComponent as MoneySvg } from "@/assets/icons/emojis/money.svg";
import { ReactComponent as MoonSvg } from "@/assets/icons/emojis/moon.svg";
import { ReactComponent as PainSvg } from "@/assets/icons/emojis/pain.svg";
import { ReactComponent as ClothesFemaleSvg } from "@/assets/icons/emojis/pink-shirt.svg";
import { ReactComponent as ProgressSvg } from "@/assets/icons/emojis/progress.svg";
import { ReactComponent as RoutineSvg } from "@/assets/icons/emojis/routine.svg";
import { ReactComponent as StarSvg } from "@/assets/icons/emojis/star.svg";
import { ReactComponent as SunSvg } from "@/assets/icons/emojis/sun.svg";
import { getDateOneMonthFromNow } from "@/components/Questions/Date";
import { ActivityLevels } from "@/constants/activityLevel";
import {
    CardioMachinesEquipmentAnswersLabels,
    EventAnswersLabels,
} from "@/constants/answersLabels";
import {
    BodyInjures,
    FEMALE_BODY_INJURES_IMG,
    FEMALE_BODY_INJURES_IMG_SELECTED,
} from "@/constants/body-injures";
import {
    BodyMetricTypes,
    FemaleBodyFatRangeTypes,
    FemaleImagesByBodyFatRange,
    UnitTypes,
} from "@/constants/bodyMetrics";
import { FitnessGoals, goalTextByIdChartImage } from "@/constants/fitnessGoal";
import { FitnessLevels, getFitnessLevelLabel } from "@/constants/fitnessLevels";
import { questionGroups } from "@/constants/questionGroups";
import { CheckboxType, QUESTIONS_IDS, QuestionTypes } from "@/constants/questionsIds";
import {
    CardioMachinesEquipment,
    Equipment,
    GymType,
    TrainingEnvironment,
} from "@/constants/trainingEnvironment";
import bodyArms from "@/female/assets/body-injures/arms.webp";
import bodyBack from "@/female/assets/body-injures/back.webp";
import bodyElbows from "@/female/assets/body-injures/elbows.webp";
import bodyFeet from "@/female/assets/body-injures/feet.webp";
import bodyKnees from "@/female/assets/body-injures/knees.webp";
import bodyLegs from "@/female/assets/body-injures/legs.webp";
import bodyNeck from "@/female/assets/body-injures/neck.webp";
import bodyNoIssues from "@/female/assets/body-injures/no-issues.webp";
import bodyShoulders from "@/female/assets/body-injures/shoulders.webp";
import assaultBike from "@/female/assets/cardio-machines/assault-bike.webp";
import elliptical from "@/female/assets/cardio-machines/elliptical.webp";
import rower from "@/female/assets/cardio-machines/rowing-machine.webp";
import statBike from "@/female/assets/cardio-machines/stat-bike.webp";
import stepper from "@/female/assets/cardio-machines/stepper.webp";
import treadmill from "@/female/assets/cardio-machines/treadmill.webp";
import treadmillRunImg from "@/female/assets/cardio-machines/treadmill-run.webp";
import gymLocation from "@/female/assets/location/gym.webp";
import homeLocation from "@/female/assets/location/home.webp";
import mixedLocation from "@/female/assets/location/mix.webp";
import muscleGainFemale from "@/female/assets/new-qs-sequence/primary-goal/gain-muscle.webp";
import getInShapeFemale from "@/female/assets/new-qs-sequence/primary-goal/get-in-shape.webp";
import weightLossFemale from "@/female/assets/new-qs-sequence/primary-goal/lose-weight.webp";
import femaleLean from "@/female/assets/target-body-type/lean.webp";
import femaleMuscular from "@/female/assets/target-body-type/muscular.webp";
import femaleRipped from "@/female/assets/target-body-type/ripped.webp";
import {
    BreathLoseAnswers,
    DailyWalkAnswers,
    DesiredBodyAnswers,
    DietTypeAnswers,
    EventAnswers,
    FeelBetweenMealsAnswers,
    FitnessExperienceAnswers,
    HealthProblemsAnswers,
    OtherSportsAnswers,
    PaceAnswers,
    PushUpsSquartsAnswers,
    SleepAmountAnswers,
    SportPreferAnswers,
    Train3MonthsAnswers,
    WaterConsumption,
    WeightLossGoal,
    YesNoAnswers,
} from "@/types/answers";
import { Genders } from "@/types/gender";
import { Question } from "@/types/question";
import { Answers } from "@/utils/AnswersProvider";

import bands from "../../assets/img/questions/equipment/bands.webp";
import brbll from "../../assets/img/questions/equipment/brbll.webp";
import dumbbell from "../../assets/img/questions/equipment/dumbbell.webp";
import kettleber from "../../assets/img/questions/equipment/kettleber.webp";
import mat from "../../assets/img/questions/equipment/mat.webp";
import none from "../../assets/img/questions/equipment/none.webp";
import pub from "../../assets/img/questions/equipment/pub.webp";
import rope from "../../assets/img/questions/equipment/rope.webp";

export const getQuestions = (
    prefix: string,
    answers: Answers,
    strapiQuestions?: Question<QuestionTypes.Radio | QuestionTypes.Checkbox>[] | null,
    isAndroid?: boolean,
    isWeightPredictor?: boolean,
    isNumberOfStepsExp?: boolean,
    isAIUserSummaryIter2Exp?: boolean
) => [
    {
        id: QUESTIONS_IDS.UNHAPPY_ABOUT_YOUR_CURRENT_FITNESS,
        event: `${prefix}_${QUESTIONS_IDS.UNHAPPY_ABOUT_YOUR_CURRENT_FITNESS}`,
        type: QuestionTypes.Checkbox,
        checkboxType: CheckboxType.tileSquare,
        title: "What makes you most unhappy about your current fitness?",
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: "clothing_fit",
                text: "Clothing fit",
                svg: DressSvg,
            },
            {
                id: "lack_of_energy",
                text: "Lack of energy",
                svg: BedSvg,
            },
            {
                id: "not_seeing_progress",
                text: "Not seeing progress",
                svg: ConfusedFemaleSvg,
            },
            {
                id: "inconsistent_routine",
                text: "Inconsistent routine",
                svg: RoutineSvg,
            },
            {
                id: "weight_gain_despite_activity",
                text: "Weight gain despite activity",
                svg: ProgressSvg,
            },
            {
                id: "pain_and_discomfort",
                text: "Pain and discomfort",
                svg: PainSvg,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.PRIMARY_GOAL,
        event: `${prefix}_${QUESTIONS_IDS.PRIMARY_GOAL}`,
        type: QuestionTypes.RadioImageLeft,
        title: "Which result would change your life the most?",
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: FitnessGoals.LoseWeight,
                text: "Lose Weight & Burn Fat",
                description: "Drop dress sizes and slim down",
                img: weightLossFemale,
            },
            {
                id: FitnessGoals.GainMuscle,
                text: "Build Muscle & Strength",
                description: "Feel stronger and lift heavier",
                img: muscleGainFemale,
            },
            {
                id: FitnessGoals.GetInShape,
                text: "Get Fit & Look Toned",
                description: "Get in shape and see muscle definition",
                img: getInShapeFemale,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.TRAIN_3_MONTHS,
        event: `${prefix}_${QUESTIONS_IDS.TRAIN_3_MONTHS}`,
        type: QuestionTypes.Radio,
        title: i18n.t("train_3months_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: Train3MonthsAnswers.HardlyEver,
                text: i18n.t("hardly_ever"),
            },
            {
                id: Train3MonthsAnswers.One2PerWeek,
                text: i18n.t("1_2_per_week"),
            },
            {
                id: Train3MonthsAnswers.Three4PerWeek,
                text: i18n.t("3_4_per_week"),
            },
            {
                id: Train3MonthsAnswers.Five6PerWeek,
                text: i18n.t("5_6_per_week"),
            },
            {
                id: Train3MonthsAnswers.Everyday,
                text: i18n.t("everyday"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.DRIVES_TO_ACHIEVE_GOALS,
        event: `${prefix}_${QUESTIONS_IDS.DRIVES_TO_ACHIEVE_GOALS}`,
        type: QuestionTypes.Checkbox,
        checkboxType: CheckboxType.square,
        title: "What drives you to achieve your fitness goals?",
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: "confidence",
                text: "Be the most confident person in the room",
                isPopular: true,
            },
            {
                id: "clothing_fit",
                text: "Never worry about what to wear again",
            },
            {
                id: "no_limits",
                text: "Do any activity without physical limitations",
            },
            {
                id: "best_self",
                text: "Feel like your best self",
                isPopular: true,
            },
            {
                id: "become inspo",
                text: "Inspire others to improve their fitness",
            },
            {
                id: "other",
                text: "Other",
            },
        ],
    },
    {
        id: QUESTIONS_IDS.HOLDING_BACK_FROM_FITNESS_GOALS,
        event: `${prefix}_${QUESTIONS_IDS.HOLDING_BACK_FROM_FITNESS_GOALS}`,
        type: QuestionTypes.Checkbox,
        checkboxType: CheckboxType.square,
        title: "What's holding you back from your fitness goals?",
        subtitle: "Select all challenges that apply and we'll help you overcome them:",
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: "busy_schedule",
                text: "Busy schedule & long work hours",
                svg: ClockSvg,
            },
            {
                id: "motivation_boosts_to_train",
                text: "Need regular motivation boosts to train",
                svg: FireSvg,
            },
            {
                id: "no_workout_plan",
                text: "No workout plan or guidance",
                svg: CalendarSvg,
            },
            {
                id: "health_restrictions",
                text: "Injuries or health restrictions",
                svg: PainSvg,
            },
            {
                id: "family_responsibilities",
                text: "Family responsibilities & commitments",
                svg: FamilySvg,
            },
            {
                id: "limited_workout_space",
                text: "Limited workout space & equipment",
                svg: HomeSvg,
            },
            {
                id: "irregular_daily_routine",
                text: "Irregular daily routine",
                svg: SunSvg,
            },
            {
                id: "budget_limitations",
                text: "Budget limitations for fitness",
                svg: MoneySvg,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.STATEMENT_TIRED_OF_GUESSING,
        type: QuestionTypes.Rate,
        event: `${prefix}_${QUESTIONS_IDS.STATEMENT_TIRED_OF_GUESSING}`,
        title: "How true is this for you?",
        groupTitle: questionGroups.Motivation,
        subtitle:
            "I want someone to just tell me exactly what exercises to do - I'm tired of guessing what's right.",
        maxRate: 5,
    },
    {
        id: QUESTIONS_IDS.ACTIVE_USERS_AND_REVIEWS,
        event: `${prefix}_${QUESTIONS_IDS.ACTIVE_USERS_AND_REVIEWS}`,
        type: QuestionTypes.ReviewAndActiveUsers,
        groupTitle: questionGroups.Goals,
    },
    {
        id: QUESTIONS_IDS.YOU_ARE_ON_THE_RIGHT_TRACK,
        event: `${prefix}_${QUESTIONS_IDS.YOU_ARE_ON_THE_RIGHT_TRACK}`,
        type: QuestionTypes.Checkbox,
        checkboxType: CheckboxType.square,
        title: "When will you know you're on the right track?",
        subtitle: "Pick your first exciting milestone",
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: "clothes_more_comfortable",
                text: "My clothes finally fit comfortably!",
                svg: ClothesFemaleSvg,
            },
            {
                id: "full_week_of_workouts",
                text: "Completed a full week of workouts!",
                svg: CalendarWithDateSvg,
            },
            {
                id: "climbing_stairs",
                text: "Climbed a flight of stairs without getting winded!",
                svg: StarSvg,
            },
            {
                id: "best_sleep",
                text: "Got my best sleep in months last night!",
                svg: MoonSvg,
            },
            {
                id: "feel_focused",
                text: "Actually feel focused and productive at work!",
                svg: BrainSvg,
            },
            {
                id: "someone_noticed_my_changes",
                text: "Someone just noticed changes in my body!",
                svg: ExcitedSvg,
            },
            {
                id: "more_energetic",
                text: "Feel more energetic throughout the day!",
                svg: EnergySvg,
            },
        ],
    },
    ...(isWeightPredictor
        ? [
              {
                  id: QUESTIONS_IDS.WEIGHT_LOSS_GOAL,
                  event: `${prefix}_${QUESTIONS_IDS.WEIGHT_LOSS_GOAL}`,
                  type: QuestionTypes.Radio,
                  title: "What is your weight loss goal?",
                  groupTitle: questionGroups.Goals,
                  answers: [
                      {
                          id: WeightLossGoal.OneToTen,
                          text:
                              answers.units === UnitTypes.Imperial
                                  ? "Lose 2.2-22 lbs for good"
                                  : "Lose 1-10 kg for good",
                      },
                      {
                          id: WeightLossGoal.ElevenToTwenty,
                          text:
                              answers.units === UnitTypes.Imperial
                                  ? "Lose 23-44 lbs for good"
                                  : "Lose 11-20 kg for good",
                      },
                      {
                          id: WeightLossGoal.TwentyPlus,
                          text:
                              answers.units === UnitTypes.Imperial
                                  ? "Lose over 44 lbs for good"
                                  : "Lose over 20 kg for good",
                      },
                      {
                          id: WeightLossGoal.MaintainWeight,
                          text: "Maintain weight and get fit",
                      },
                      {
                          id: WeightLossGoal.NotDecided,
                          text: "I haven't decided yet",
                      },
                  ],
              },
          ]
        : []),
    {
        id: QUESTIONS_IDS.DESIRED_BODY,
        event: `${prefix}_${QUESTIONS_IDS.DESIRED_BODY}`,
        type: QuestionTypes.RadioImageLeft,
        title: i18n.t("target_body_prompt"),
        subtitle: i18n.t("target_body_motivation", {
            context: Genders.Female,
            defaultValue: "target_body_motivation",
        }),
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: DesiredBodyAnswers.Lean,
                text: i18n.t("lean", {
                    context: Genders.Female,
                    defaultValue: "lean",
                }),
                img: femaleLean,
            },
            {
                id: DesiredBodyAnswers.Muscular,
                text: i18n.t("toned"),
                img: femaleMuscular,
            },
            {
                id: DesiredBodyAnswers.Ripped,
                text: i18n.t("fit"),
                img: femaleRipped,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.PROMO_SOCIAL_PROOF,
        event: `${prefix}_${QUESTIONS_IDS.PROMO_SOCIAL_PROOF}`,
        type: QuestionTypes.PromoSocialProof,
    },
    {
        id: QUESTIONS_IDS.TARGET_AREAS,
        event: `${prefix}_${QUESTIONS_IDS.TARGET_AREAS}`,
        type: QuestionTypes.CheckboxInline,
        title: i18n.t("focus_areas_prompt"),
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: BodyInjures.FullBody,
                text: i18n.t("full_body"),
                img: FEMALE_BODY_INJURES_IMG[BodyInjures.FullBody],
                selectedImg: FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.FullBody],
            },
            {
                id: BodyInjures.Shoulders,
                text: i18n.t("shoulders"),
                img: FEMALE_BODY_INJURES_IMG[BodyInjures.Shoulders],
                selectedImg: FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Shoulders],
            },
            {
                id: BodyInjures.Arms,
                text: i18n.t("arms"),
                img: FEMALE_BODY_INJURES_IMG[BodyInjures.Arms],
                selectedImg: FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Arms],
            },
            {
                id: BodyInjures.Core,
                text: i18n.t("back"),
                img: FEMALE_BODY_INJURES_IMG[BodyInjures.Core],
                selectedImg: FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Core],
            },
            {
                id: BodyInjures.Chest,
                text: i18n.t("chest"),
                img: FEMALE_BODY_INJURES_IMG[BodyInjures.Chest],
                selectedImg: FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Chest],
            },
            {
                id: BodyInjures.Abs,
                text: i18n.t("belly"),
                img: FEMALE_BODY_INJURES_IMG[BodyInjures.Abs],
                selectedImg: FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Abs],
            },
            {
                id: BodyInjures.Glutes,
                text: i18n.t("butt"),
                img: FEMALE_BODY_INJURES_IMG[BodyInjures.Glutes],
                selectedImg: FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Glutes],
            },
            {
                id: BodyInjures.Legs,
                text: i18n.t("legs"),
                img: FEMALE_BODY_INJURES_IMG[BodyInjures.Legs],
                selectedImg: FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Legs],
            },
        ],
    },
    {
        id: QUESTIONS_IDS.BODY_FAT,
        event: `${prefix}_${QUESTIONS_IDS.BODY_FAT}`,
        type: QuestionTypes.Range,
        groupTitle: isNumberOfStepsExp ? questionGroups.Goals : questionGroups.Personal,
        title: i18n.t("body_fat_level_prompt"),
        answers: Object.values(FemaleBodyFatRangeTypes).map((type) => ({
            id: type,
            value: type,
            img: FemaleImagesByBodyFatRange[type],
        })),
        ...(isWeightPredictor ? {} : { progressBarBreakPoint: true }),
    },
    ...(isWeightPredictor
        ? [
              {
                  id: QUESTIONS_IDS.PROMO_ZING_AI,
                  event: `${prefix}_${QUESTIONS_IDS.PROMO_ZING_AI}`,
                  type: QuestionTypes.PromoZingAi,
                  groupTitle: isNumberOfStepsExp ? "" : questionGroups.ZingAi,
                  progressBarBreakPoint: true,
              },
              {
                  id: QUESTIONS_IDS.HEIGHT,
                  event: `${prefix}_${QUESTIONS_IDS.HEIGHT}`,
                  type: QuestionTypes.Input,
                  inputType: BodyMetricTypes.HEIGHT,
                  title: i18n.t("height_prompt"),
                  groupTitle: isNumberOfStepsExp
                      ? questionGroups.BodyMetrics
                      : questionGroups.Personal,
              },
              {
                  id: QUESTIONS_IDS.CURRENT_WEIGHT,
                  event: `${prefix}_${QUESTIONS_IDS.CURRENT_WEIGHT}`,
                  type: QuestionTypes.Input,
                  inputType: BodyMetricTypes.WEIGHT,
                  title: i18n.t("current_weight_prompt"),
                  groupTitle: isNumberOfStepsExp
                      ? questionGroups.BodyMetrics
                      : questionGroups.Personal,
              },
              {
                  id: QUESTIONS_IDS.TARGET_WEIGHT,
                  event: `${prefix}_${QUESTIONS_IDS.TARGET_WEIGHT}`,
                  type: QuestionTypes.Input,
                  inputType: BodyMetricTypes.WEIGHT,
                  weightType: "target",
                  title: i18n.t("target_weight_prompt"),
                  groupTitle: isNumberOfStepsExp
                      ? questionGroups.BodyMetrics
                      : questionGroups.Personal,
              },
              {
                  id: QUESTIONS_IDS.AGE,
                  event: `${prefix}_${QUESTIONS_IDS.AGE}`,
                  type: QuestionTypes.Input,
                  inputType: BodyMetricTypes.AGE,
                  title: i18n.t("age_prompt"),
                  groupTitle: isNumberOfStepsExp
                      ? questionGroups.BodyMetrics
                      : questionGroups.Personal,
              },
              {
                  id: QUESTIONS_IDS.PACE,
                  event: `${prefix}_${QUESTIONS_IDS.PACE}`,
                  type: QuestionTypes.Radio,
                  title: "Your Plan, Your Pace",
                  subtitle: "You know what works best for you. Which pace would you prefer?",
                  groupTitle: isNumberOfStepsExp
                      ? questionGroups.BodyMetrics
                      : questionGroups.Personal,
                  answers: [
                      {
                          id: PaceAnswers.Fast,
                          text: "As fast as possible",
                      },
                      {
                          id: PaceAnswers.Steady,
                          text: "Slow and steady wins the race",
                      },
                      {
                          id: PaceAnswers.BetweenTwo,
                          text: "Somewhere in between the two",
                      },
                  ],
              },
              {
                  id: QUESTIONS_IDS.INITIAL_WEIGHT_PREDICT,
                  event: `${prefix}_${QUESTIONS_IDS.INITIAL_WEIGHT_PREDICT}`,
                  type: QuestionTypes.InitialWeightPredict,
                  groupTitle: isNumberOfStepsExp ? "" : questionGroups.Personal,
              },
          ]
        : []),
    {
        id: QUESTIONS_IDS.FITNESS_LEVEL,
        event: `${prefix}_${QUESTIONS_IDS.FITNESS_LEVEL}`,
        type: QuestionTypes.Radio,
        title: i18n.t("fitness_level_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: FitnessLevels.Beginner,
                text: i18n.t(FitnessLevels.Beginner),
                description: i18n.t("beginner_description", {
                    context: Genders.Female,
                    defaultValue: "beginner_description",
                }),
            },
            {
                id: FitnessLevels.Intermediate,
                text: getFitnessLevelLabel(FitnessLevels.Intermediate, Genders.Female),
                description: i18n.t("intermediate_description"),
            },
            {
                id: FitnessLevels.Advanced,
                text: getFitnessLevelLabel(FitnessLevels.Advanced, Genders.Female),
                description: i18n.t("advanced_description"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.FITNESS_EXPERIENCE,
        event: `${prefix}_${QUESTIONS_IDS.FITNESS_EXPERIENCE}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("fitness_experience_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: FitnessExperienceAnswers.PersonalTrainers,
                text: i18n.t("personal_trainers"),
            },
            {
                id: FitnessExperienceAnswers.FitnessApps,
                text: i18n.t("fitness_apps"),
            },
            {
                id: FitnessExperienceAnswers.WorkoutVideos,
                text: i18n.t("workout_videos"),
            },
            {
                id: FitnessExperienceAnswers.SelfTaught,
                text: i18n.t("self_taught"),
            },
            {
                id: FitnessExperienceAnswers.None,
                text: i18n.t("none"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.HEALTH_PROBLEMS,
        event: `${prefix}_${QUESTIONS_IDS.HEALTH_PROBLEMS}`,
        type: QuestionTypes.CheckboxImageLeft,
        title: i18n.t("injuries_prompt"),
        subtitle: i18n.t("injuries_motivation_prompt"),
        groupTitle: questionGroups.Fitness,
        emptyAnswer: {
            id: HealthProblemsAnswers.None,
            text: i18n.t("no_issues"),
            img: bodyNoIssues,
        },
        answers: [
            {
                id: HealthProblemsAnswers.BackPain,
                text: i18n.t("back"),
                img: bodyBack,
            },
            {
                id: HealthProblemsAnswers.KneePain,
                text: i18n.t("knees"),
                img: bodyKnees,
            },
            {
                id: HealthProblemsAnswers.ShoulderPain,
                text: i18n.t("shoulders"),
                img: bodyShoulders,
            },
            {
                id: HealthProblemsAnswers.NeckPain,
                text: i18n.t("neck"),
                img: bodyNeck,
            },
            {
                id: HealthProblemsAnswers.ArmPain,
                text: i18n.t("arms"),
                img: bodyArms,
            },
            {
                id: HealthProblemsAnswers.ElbowPain,
                text: i18n.t("elbows"),
                img: bodyElbows,
            },
            {
                id: HealthProblemsAnswers.LegPain,
                text: i18n.t("legs"),
                img: bodyLegs,
            },
            {
                id: HealthProblemsAnswers.FootPain,
                text: i18n.t("feet"),
                img: bodyFeet,
            },
        ],
    },
    ...(isWeightPredictor
        ? [
              {
                  id: QUESTIONS_IDS.BREATH_LOSE,
                  event: `${prefix}_${QUESTIONS_IDS.BREATH_LOSE}`,
                  type: QuestionTypes.Radio,
                  title: "Do you lose your breath when climbing stairs?",
                  groupTitle: isNumberOfStepsExp ? questionGroups.Fitness : questionGroups.Goals,
                  answers: [
                      {
                          id: BreathLoseAnswers.CantCatchMyBreath,
                          text: "I'm so tired I can't catch my breath",
                      },
                      {
                          id: BreathLoseAnswers.OutOfBreath,
                          text: "I'm a little out of breath, but I can talk",
                      },
                      {
                          id: BreathLoseAnswers.FeelGoodAfterClimbing,
                          text: "I feel good after climbing the stairs",
                      },
                      {
                          id: BreathLoseAnswers.CanHandleSeveralFlights,
                          text: "I can handle several flights of stairs",
                      },
                  ],
              },
          ]
        : [
              {
                  id: QUESTIONS_IDS.PUSH_UPS,
                  event: `${prefix}_${QUESTIONS_IDS.PUSH_UPS}`,
                  type: QuestionTypes.Radio,
                  title: i18n.t("push_ups_prompt"),
                  groupTitle: questionGroups.Fitness,
                  answers: [
                      {
                          id: PushUpsSquartsAnswers.Few,
                          text: i18n.t("fewer_than_12"),
                      },
                      {
                          id: PushUpsSquartsAnswers.Average,
                          text: i18n.t("13_to_20"),
                      },
                      {
                          id: PushUpsSquartsAnswers.Lot,
                          text: i18n.t("more_than_20"),
                      },
                      {
                          id: "no_info",
                          text: i18n.t("dont_know"),
                      },
                  ],
              },
          ]),
    {
        id: QUESTIONS_IDS.SQUATS,
        event: `${prefix}_${QUESTIONS_IDS.SQUATS}`,
        type: QuestionTypes.Radio,
        title: i18n.t("squats_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: PushUpsSquartsAnswers.Few,
                text: i18n.t("fewer_than_12"),
            },
            {
                id: PushUpsSquartsAnswers.Average,
                text: i18n.t("13_to_20"),
            },
            {
                id: PushUpsSquartsAnswers.Lot,
                text: i18n.t("more_than_20"),
            },
            {
                id: PushUpsSquartsAnswers.NoInfo,
                text: i18n.t("dont_know"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.OTHER_SPORTS,
        event: `${prefix}_${QUESTIONS_IDS.OTHER_SPORTS}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("other_activities_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: OtherSportsAnswers.No,
                text: i18n.t("no"),
            },
            {
                id: OtherSportsAnswers.Cardio,
                text: i18n.t("cardio"),
                description: i18n.t("cardio_types"),
            },
            {
                id: OtherSportsAnswers.Flexibility,
                text: i18n.t("flexibility"),
                description: i18n.t("flexibility_types"),
            },
            {
                id: OtherSportsAnswers.MartialArts,
                text: i18n.t("martial_arts"),
                description: i18n.t("martial_arts_types"),
            },
            {
                id: OtherSportsAnswers.GameSports,
                text: i18n.t("sports"),
                description: i18n.t("game_sports_types"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.DAILY_WALK,
        event: `${prefix}_${QUESTIONS_IDS.DAILY_WALK}`,
        type: QuestionTypes.Radio,
        title: i18n.t("daily_walk_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: DailyWalkAnswers.Few,
                text: i18n.t("less_than_1_hour"),
            },
            {
                id: DailyWalkAnswers.Average,
                text: i18n.t("1_to_2_hours"),
            },
            {
                id: DailyWalkAnswers.Lot,
                text: i18n.t("more_than_2_hours"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.ACTIVITY_LEVEL,
        event: `${prefix}_${QUESTIONS_IDS.ACTIVITY_LEVEL}`,
        type: QuestionTypes.Radio,
        title: i18n.t("activity_level_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: ActivityLevels.Sedentary,
                text: i18n.t("sedentary"),
                description: i18n.t("minimal_activity"),
            },
            {
                id: ActivityLevels.ModerateExercise,
                text: i18n.t("moderately_active", {
                    context: Genders.Female,
                    defaultValue: i18n.t("moderately_active"),
                }),
                description: i18n.t("some_movement"),
            },
            {
                id: ActivityLevels.Athlete,
                text: i18n.t("very_active"),
                description: i18n.t("physically_demanding"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.FEEL_BETWEEN_MEALS,
        event: `${prefix}_${QUESTIONS_IDS.FEEL_BETWEEN_MEALS}`,
        type: QuestionTypes.Radio,
        title: i18n.t("between_meals_feelings_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: FeelBetweenMealsAnswers.Sleepy,
                text: i18n.t("sleepy_when_hungry"),
            },
            {
                id: FeelBetweenMealsAnswers.Tired,
                text: i18n.t("tired_after_eating"),
            },
            {
                id: FeelBetweenMealsAnswers.Energized,
                text: i18n.t("always_energized"),
            },
        ],
        ...(isNumberOfStepsExp ? { progressBarBreakPoint: true } : {}),
    },
    {
        id: QUESTIONS_IDS.SLEEP_AMOUNT,
        event: `${prefix}_${QUESTIONS_IDS.SLEEP_AMOUNT}`,
        type: QuestionTypes.Radio,
        title: i18n.t("sleep_prompt"),
        groupTitle: isNumberOfStepsExp ? questionGroups.Personal : questionGroups.Lifestyle,
        answers: [
            {
                id: SleepAmountAnswers.LessThan5,
                text: i18n.t("fewer_than_5_hours"),
            },
            {
                id: SleepAmountAnswers.From5To6,
                text: i18n.t("5_to_6_hours"),
            },
            {
                id: SleepAmountAnswers.From7To8,
                text: i18n.t("7_to_8_hours"),
            },
            {
                id: SleepAmountAnswers.Over8,
                text: i18n.t("over_8_hours"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.WATER_CONSUMPTION,
        event: `${prefix}_${QUESTIONS_IDS.WATER_CONSUMPTION}`,
        type: QuestionTypes.Radio,
        title: i18n.t("water_prompt"),
        groupTitle: isNumberOfStepsExp ? questionGroups.Personal : questionGroups.Lifestyle,
        answers: [
            {
                id: WaterConsumption.NoWater,
                text: i18n.t("only_tea_coffee"),
            },
            {
                id: WaterConsumption.Low,
                text: i18n.t("fewer_than_2_glasses"),
            },
            {
                id: WaterConsumption.Average,
                text: i18n.t("2_to_6_glasses"),
            },
            {
                id: WaterConsumption.AboveAverage,
                text: i18n.t("7_to_10_glasses"),
            },
            {
                id: WaterConsumption.Lot,
                text: i18n.t("more_than_10_glasses"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.DIET_TYPE,
        type: QuestionTypes.Radio,
        // TODO: review analytics event name
        event: `${prefix}_dietary_preferences`,
        title: i18n.t("dietary_preferences_prompt"),
        groupTitle: isNumberOfStepsExp ? questionGroups.Personal : questionGroups.Lifestyle,
        answers: [
            {
                id: DietTypeAnswers.NoDiet,
                text: i18n.t("none"),
            },
            {
                id: DietTypeAnswers.Diabetes,
                text: i18n.t("diabetes"),
            },
            {
                id: DietTypeAnswers.Vegan,
                text: i18n.t("vegetarian_vegan"),
            },
            {
                id: DietTypeAnswers.GlutenFree,
                text: i18n.t("gluten_free"),
            },
            {
                id: DietTypeAnswers.LactoseFree,
                text: i18n.t("lactose_free"),
            },
            {
                id: DietTypeAnswers.NutAllergy,
                text: i18n.t("nut_allergy"),
            },
        ],
    },

    {
        id: QUESTIONS_IDS.TOO_EXPENSIVE_FITNESS_PROGRAM_FEE,
        event: `${prefix}_${QUESTIONS_IDS.TOO_EXPENSIVE_FITNESS_PROGRAM_FEE}`,
        title: (
            <Trans i18nKey="too_expensive_fitness_program_fee_prompt">
                <span className="text-zing-warm-tangerine-500" />
            </Trans>
        ),
        subtitle: i18n.t("enter_amount"),
        groupTitle: questionGroups.Lifestyle,
        type: QuestionTypes.CurrencyInput,
    },
    {
        id: QUESTIONS_IDS.PLAN_FAILED,
        event: `${prefix}_${QUESTIONS_IDS.PLAN_FAILED}`,
        type: QuestionTypes.Radio,
        title: (
            <Trans
                i18nKey="fitness_plan_failure_prompt"
                values={{
                    goal: goalTextByIdChartImage[
                        answers[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals
                    ],
                }}
            >
                <span className="text-white female:text-zing-warm-tangerine-500" />
            </Trans>
        ),
        groupTitle: isNumberOfStepsExp ? questionGroups.Personal : questionGroups.Fitness,
        answers: [
            {
                id: YesNoAnswers.Yes,
                text: i18n.t("yes"),
            },
            {
                id: YesNoAnswers.No,
                text: i18n.t("no"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.PROMO_HELP,
        event: `${prefix}_${QUESTIONS_IDS.PROMO_HELP}`,
        type: QuestionTypes.PromoHelp,
        ...(isNumberOfStepsExp ? {} : { progressBarBreakPoint: true }),
    },
    {
        id: QUESTIONS_IDS.LOCATION,
        event: `${prefix}_${QUESTIONS_IDS.LOCATION}`,
        type: QuestionTypes.RadioImageLeft,
        title: i18n.t("choose_location"),
        groupTitle: isNumberOfStepsExp ? questionGroups.Personal : questionGroups.Location,
        answers: [
            {
                id: TrainingEnvironment.Home,
                text: i18n.t("home"),
                description: i18n.t("home_description"),
                img: homeLocation,
            },
            {
                id: TrainingEnvironment.Gym,
                text: i18n.t("gym"),
                description: i18n.t("gym_description"),
                img: gymLocation,
            },
            {
                id: TrainingEnvironment.Mix,
                text: i18n.t("mix"),
                description: i18n.t("mixed_description"),
                img: mixedLocation,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.GYM_TYPE,
        event: `${prefix}_${QUESTIONS_IDS.GYM_TYPE}`,
        type: QuestionTypes.Radio,
        title: i18n.t("gym_type_prompt"),
        subtitle: i18n.t("equipment_suggestion"),
        groupTitle: isNumberOfStepsExp ? questionGroups.Personal : questionGroups.Location,
        answers: [
            {
                id: GymType.Large,
                text: i18n.t("large_gym"),
                description: i18n.t("large_gym_types"),
            },
            {
                id: GymType.Small,
                text: i18n.t("small_gym"),
                description: i18n.t("small_gym_types"),
            },
            {
                id: GymType.Garage,
                text: i18n.t("garage_gym"),
                description: i18n.t("garage_gym_types"),
            },
            {
                id: GymType.Home,
                text: i18n.t("home_gym"),
                description: i18n.t("home_gym_types"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.EQUIPMENT_BY_ENVIRONMENT,
        event: `${prefix}_${QUESTIONS_IDS.EQUIPMENT_BY_ENVIRONMENT}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("home_equipment_prompt"),
        groupTitle: isNumberOfStepsExp
            ? questionGroups.Personal
            : questionGroups.WorkoutPreferences,
        answers: [
            {
                id: Equipment.Dumbbells,
                text: i18n.t("dumbbells"),
                img: dumbbell,
            },
            {
                id: Equipment.YogaMat,
                text: i18n.t("yoga_mat"),
                img: mat,
            },
            {
                id: Equipment.Barbell,
                text: i18n.t("barbell"),
                img: brbll,
            },
            {
                id: Equipment.JumpRope,
                text: i18n.t("jump_rope"),
                img: rope,
            },
            {
                id: Equipment.PullUps,
                text: i18n.t("pull_up_bar"),
                img: pub,
            },
            {
                id: Equipment.PowerBand,
                text: i18n.t("power_band"),
                img: bands,
            },
            {
                id: Equipment.Kettlebell,
                text: i18n.t("kettlebells"),
                img: kettleber,
            },
            {
                id: Equipment.No,
                text: i18n.t("none_of_the_above"),
                img: none,
            },
        ],
    },
    ...(!isAndroid
        ? [
              {
                  id: QUESTIONS_IDS.ADD_CARDIO_TO_PLAN,
                  event: `${prefix}_${QUESTIONS_IDS.ADD_CARDIO_TO_PLAN}`,
                  type: QuestionTypes.YesNoWithImage,
                  title: i18n.t("add_cardio_prompt"),
                  groupTitle: questionGroups.WorkoutPreferences,
                  img: treadmillRunImg,
                  answers: [
                      {
                          id: YesNoAnswers.Yes,
                          text: i18n.t("yes"),
                      },
                      {
                          id: YesNoAnswers.No,
                          text: i18n.t("not_now"),
                      },
                  ],
              },
              ...(answers[QUESTIONS_IDS.ADD_CARDIO_TO_PLAN] === YesNoAnswers.Yes
                  ? [
                        {
                            id: QUESTIONS_IDS.SELECT_CARDIO_MACHINES,
                            event: `${prefix}_${QUESTIONS_IDS.SELECT_CARDIO_MACHINES}`,
                            type: QuestionTypes.CheckboxImageLeft,
                            title: i18n.t("cardio_machines_equipment_prompt"),
                            groupTitle: questionGroups.WorkoutPreferences,
                            answers: [
                                {
                                    id: CardioMachinesEquipment.Treadmill,
                                    text: CardioMachinesEquipmentAnswersLabels[
                                        CardioMachinesEquipment.Treadmill
                                    ],
                                    img: treadmill,
                                },
                                {
                                    id: CardioMachinesEquipment.StationaryBike,
                                    text: CardioMachinesEquipmentAnswersLabels[
                                        CardioMachinesEquipment.StationaryBike
                                    ],
                                    img: statBike,
                                },
                                {
                                    id: CardioMachinesEquipment.AssaultBike,
                                    text: CardioMachinesEquipmentAnswersLabels[
                                        CardioMachinesEquipment.AssaultBike
                                    ],
                                    img: assaultBike,
                                },
                                {
                                    id: CardioMachinesEquipment.Elliptical,
                                    text: CardioMachinesEquipmentAnswersLabels[
                                        CardioMachinesEquipment.Elliptical
                                    ],
                                    img: elliptical,
                                },
                                {
                                    id: CardioMachinesEquipment.Stepper,
                                    text: CardioMachinesEquipmentAnswersLabels[
                                        CardioMachinesEquipment.Stepper
                                    ],
                                    img: stepper,
                                },
                                {
                                    id: CardioMachinesEquipment.Rower,
                                    text: CardioMachinesEquipmentAnswersLabels[
                                        CardioMachinesEquipment.Rower
                                    ],
                                    img: rower,
                                },
                            ],
                        },
                    ]
                  : []),
          ]
        : []),
    {
        id: QUESTIONS_IDS.SPORT_PREFER,
        event: `${prefix}_${QUESTIONS_IDS.SPORT_PREFER}`,
        type: QuestionTypes.Radio,
        title: i18n.t("workout_preference_prompt"),
        groupTitle: isNumberOfStepsExp
            ? questionGroups.Personal
            : questionGroups.WorkoutPreferences,
        answers: [
            {
                id: SportPreferAnswers.Morning,
                text: i18n.t("morning"),
            },
            {
                id: SportPreferAnswers.Afternoon,
                text: i18n.t("afternoon"),
            },
            {
                id: SportPreferAnswers.Evening,
                text: i18n.t("evening"),
            },
            {
                id: SportPreferAnswers.Different,
                text: i18n.t("different_times"),
            },
        ],
    },
    ...(isWeightPredictor
        ? []
        : [
              {
                  id: QUESTIONS_IDS.HEIGHT,
                  event: `${prefix}_${QUESTIONS_IDS.HEIGHT}`,
                  type: QuestionTypes.Input,
                  inputType: BodyMetricTypes.HEIGHT,
                  title: i18n.t("height_prompt"),
                  groupTitle: questionGroups.Personal,
              },
              {
                  id: QUESTIONS_IDS.CURRENT_WEIGHT,
                  event: `${prefix}_${QUESTIONS_IDS.CURRENT_WEIGHT}`,
                  type: QuestionTypes.Input,
                  inputType: BodyMetricTypes.WEIGHT,
                  title: i18n.t("current_weight_prompt"),
                  groupTitle: questionGroups.Personal,
              },
              {
                  id: QUESTIONS_IDS.TARGET_WEIGHT,
                  event: `${prefix}_${QUESTIONS_IDS.TARGET_WEIGHT}`,
                  type: QuestionTypes.Input,
                  inputType: BodyMetricTypes.WEIGHT,
                  weightType: "target",
                  title: i18n.t("target_weight_prompt"),
                  groupTitle: questionGroups.Personal,
              },
              {
                  id: QUESTIONS_IDS.AGE,
                  event: `${prefix}_${QUESTIONS_IDS.AGE}`,
                  type: QuestionTypes.Input,
                  inputType: BodyMetricTypes.AGE,
                  title: i18n.t("age_prompt"),
                  groupTitle: questionGroups.Personal,
              },
          ]),
    ...(isAIUserSummaryIter2Exp
        ? []
        : [
              {
                  id: QUESTIONS_IDS.BMI_RESULTS,
                  event: `${prefix}_${QUESTIONS_IDS.BMI_RESULTS}`,
                  type: QuestionTypes.BmiPromoFemale,
              },
          ]),
    {
        id: QUESTIONS_IDS.EVENT_MOTIVATION,
        event: `${prefix}_${QUESTIONS_IDS.EVENT_MOTIVATION}`,
        type: QuestionTypes.Radio,
        title: (
            <Trans
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                i18nKey="important_event_prompt"
                values={{
                    goal: goalTextByIdChartImage[
                        answers[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals
                    ],
                }}
                context={Genders.Female}
                defaultValue={i18n.t("important_event_prompt")}
            >
                <span className="text-white female:text-zing-warm-tangerine-500" />
            </Trans>
        ),
        subtitle: i18n.t("important_event_motivation"),
        groupTitle: questionGroups.Personal,
        answers: [
            {
                id: EventAnswers.Vacation,
                text: EventAnswersLabels.vacation,
            },
            {
                id: EventAnswers.Wedding,
                text: EventAnswersLabels.wedding,
            },
            {
                id: EventAnswers.Reunion,
                text: EventAnswersLabels.reunion,
            },
            {
                id: EventAnswers.Birthday,
                text: EventAnswersLabels.birthday,
            },
            {
                id: EventAnswers.None,
                text: EventAnswersLabels.none,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.EVENT_DATE,
        event: `${prefix}_${QUESTIONS_IDS.EVENT_DATE}`,
        type: QuestionTypes.Date,
        value: getDateOneMonthFromNow(),
        title:
            answers[QUESTIONS_IDS.EVENT_MOTIVATION] &&
            ["sports", "summer"].includes(answers[QUESTIONS_IDS.EVENT_MOTIVATION] as string)
                ? i18n.t("event_timing_prompt")
                : i18n.t("conditional_event_timing_prompt", {
                      event: EventAnswersLabels[
                          answers[QUESTIONS_IDS.EVENT_MOTIVATION] as EventAnswers
                      ]?.toLowerCase(),
                  }),
        minDate: new Date().toISOString().split("T")[0],
        groupTitle: questionGroups.Personal,
    },
    ...(strapiQuestions ? strapiQuestions : []),
];
